'use client';

import { useState, useEffect, useMemo } from 'react';
import EcoImg from 'eco-design/base/eco-img';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';

import { Popover, message } from 'antd';
import styles from './index.module.scss';
import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/i18n';
import { useGlobal } from '@/utils/useGlobal';
import EfModal from '@/components/UI/Modal';
import { getPageRewards } from '@/service/referral';
import { ICouponDetail, ShareType, CouponType, UseStatus } from '@/components/Referral/index.d';
import { getNotes } from '@/components/Referral/util';
import { QuestionIcon, CopyIcon } from '@/components/UI/svg/Referral';
import { SuccessIcon } from '@/components/UI/svg/Ecocredits';
import { ArrowIcon } from '@/components/UI/svg/Referral';

/** 默认显示数量 */
const DEFAULT_DISPLAY_COUNT = 3;

/** 支持的优惠券类型 */
const SUPPORTED_COUPON_TYPES = [CouponType.COUPONS, CouponType.GIFT_CARD, CouponType.THIRD_COUPONS];

interface SpecialRewardsProps {
    planId?: string;
}

/** 根据优惠券类型获取折扣信息 */
function getDiscountInfo(coupon: ICouponDetail) {
    const { prizeType, rules, discountName } = coupon;

    switch (prizeType) {
        case CouponType.COUPONS:
            return {
                discount: rules?.amount || '0',
                discountUnit: rules?.discountType === 'percentage' ? '%' : rules?.currencySymbol || '$',
            };
        case CouponType.GIFT_CARD:
            return {
                discount: rules?.amount || '0',
                discountUnit: rules?.currencySymbol || '$',
            };
        case CouponType.THIRD_COUPONS:
            return {
                discount: discountName || '',
                discountUnit: '',
            };
        default:
            return {
                discount: '0',
                discountUnit: '',
            };
    }
}

export default function SpecialRewards({ planId }: SpecialRewardsProps) {
    const { t } = useLang('SpecialRewards');
    const { t: referralT } = useLang('referral');
    const { t: sharedT } = useLang('shared');

    const { locale } = getLocal();
    const { isMobile, useRouter } = useGlobal();
    const router = useRouter();
    const { timezoneList } = useMemberTimeStore();

    const [couponList, setCouponList] = useState<ICouponDetail[]>([]);
    const [showAll, setShowAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [shopPopVisible, setShopPopVisible] = useState(false);
    const [currentCoupon, setCurrentCoupon] = useState<ICouponDetail | null>(null);

    // 获取优惠券数据
    useEffect(() => {
        const fetchRewards = async () => {
            setLoading(true);
            try {
                const params = {
                    shareType: ShareType.RECEIVER,
                    size: -1,
                    ...(planId ? { planId } : {}),
                };
                const res = await getPageRewards(params, locale);
                if (res.code === '0' && res.data?.content) {
                    // 过滤只支持的类型
                    const filtered = res.data.content.filter((item) => SUPPORTED_COUPON_TYPES.includes(item.prizeType));
                    setCouponList(filtered);
                }
            } catch (error) {
                console.error('Failed to fetch rewards:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRewards();
    }, [planId, locale]);

    // 显示的优惠券列表
    const displayList = useMemo(() => {
        if (showAll) return couponList;
        return couponList.slice(0, DEFAULT_DISPLAY_COUNT);
    }, [couponList, showAll]);

    // 复制优惠码
    const copyCode = (code: string, coupon: ICouponDetail) => {
        if (!code || coupon.useStatus !== UseStatus.UNUSED) return;

        if (navigator?.clipboard && window?.isSecureContext) {
            navigator.clipboard.writeText(code);
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = code;
            textArea.style.position = 'absolute';
            textArea.style.opacity = '0';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
        }

        setCurrentCoupon(coupon);
        setShopPopVisible(true);
    };

    // 仅复制，不弹窗
    const copyCodeOnly = (e: React.MouseEvent, code: string) => {
        e.stopPropagation();
        if (!code) return;

        if (navigator?.clipboard && window?.isSecureContext) {
            navigator.clipboard.writeText(code);
            message.success(sharedT('my.site_account_profile_save_success'));
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = code;
            textArea.style.position = 'absolute';
            textArea.style.opacity = '0';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
            message.success(sharedT('my.site_account_profile_save_success'));
        }
    };

    // 去购物
    const handleShopNow = () => {
        if (!currentCoupon) return;

        const { jumpUrl } = currentCoupon.rules || {};

        if (currentCoupon.prizeType === CouponType.THIRD_COUPONS) {
            if (isMobile) {
                router.push(jumpUrl);
            } else {
                window.open(jumpUrl, '_blank');
            }
            setShopPopVisible(false);
            return;
        }

        if (jumpUrl) {
            if (jumpUrl.startsWith('http')) {
                window.open(jumpUrl, '_blank');
            } else {
                window.location.href = jumpUrl;
            }
        } else {
            const storeUrl = `https://${locale}.ecoflow.com`;
            if (isMobile) {
                router.push(storeUrl);
            } else {
                window.open(storeUrl, '_blank');
            }
        }
        setShopPopVisible(false);
    };

    // 获取按钮文案
    const getShopButtonText = () => {
        if (!currentCoupon) return referralT('store');
        if (currentCoupon.prizeType === CouponType.THIRD_COUPONS) {
            return referralT('spend_now');
        }
        return referralT('store');
    };

    // 渲染规则弹窗内容
    const renderRulesContent = (coupon: ICouponDetail) => {
        const rules = getNotes(coupon.rules, referralT, locale, timezoneList);
        return (
            <div className="rules-popover">
                <h6 className="title">{referralT('rule2')}</h6>
                <ul className="rules-list">
                    {rules.map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                    ))}
                </ul>
            </div>
        );
    };

    // 渲染单个优惠券卡片
    const renderCouponCard = (coupon: ICouponDetail, index: number) => {
        const { discount, discountUnit } = getDiscountInfo(coupon);
        const isUnused = coupon.useStatus === UseStatus.UNUSED;
        const isPercentage = coupon.rules?.discountType === 'percentage';

        return (
            <div
                key={coupon.prizeLogId || index}
                className={`coupon-card ${!isUnused ? 'disabled' : ''}`}
                onClick={() => isUnused && copyCode(coupon.coupon, coupon)}
            >
                <div className="coupon-left">
                    <div className="bg-wrapper">
                        <EcoImg
                            pc="https://websiteoss.ecoflow.com/figma-uploads/1766566838922-by1zom-compressed-Vector.png"
                            pad="https://websiteoss.ecoflow.com/figma-uploads/1766567107221-ec2pjp-compressed-Vector.png"
                        />
                    </div>
                    <span className="coupon-text">COUPON</span>
                    <div className="coupon-rules">
                        <span className="rules-text">{referralT('rule2')}</span>
                        <Popover overlayClassName={styles.rules_pop} content={renderRulesContent(coupon)} trigger="click" placement="top">
                            <span className="rules-icon" onClick={(e) => e.stopPropagation()}>
                                <QuestionIcon />
                            </span>
                        </Popover>
                    </div>
                </div>
                <div className="coupon-right">
                    <div className="discount-box">
                        {/* 第三方优惠券直接显示 discountName */}
                        {coupon.prizeType === CouponType.THIRD_COUPONS ? (
                            <span className="discount-name">{discount}</span>
                        ) : (
                            <>
                                <span className="discount-number">{discount}</span>
                                <div className="discount-unit">
                                    <span className="unit">{discountUnit}</span>
                                    {isPercentage && <span className="off"> {referralT('coupon_off')}</span>}
                                </div>
                            </>
                        )}
                    </div>
                    {isUnused ? (
                        <div className="code-box">
                            <span className="code-label">{referralT('coupon_code')}:</span>
                            <span className="code-value">{coupon.coupon}</span>
                            <button className="copy-btn" onClick={(e) => copyCodeOnly(e, coupon.coupon)}>
                                <CopyIcon />
                            </button>
                        </div>
                    ) : (
                        <div className="status-box">
                            <span className="status-text">{coupon.useStatus === UseStatus.USED ? 'USED' : 'EXPIRED'}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // 空数据或加载中不显示
    if (loading || couponList.length === 0) return null;

    return (
        <div className={`${styles.SpecialRewards} GtmModuleData-section`}>
            <div className="sh-layout">
                <div className="special-rewards-header">
                    <h2 className="special-rewards-title">{t('title')}</h2>
                    <p className="special-rewards-desc">{t('desc')}</p>
                </div>

                <div className="coupon-list">{displayList.map((coupon, index) => renderCouponCard(coupon, index))}</div>

                {couponList.length > DEFAULT_DISPLAY_COUNT && (
                    <div className={`view-more ${showAll ? 'view-less' : ''}`} onClick={() => setShowAll(!showAll)}>
                        {showAll ? referralT('view_less') : referralT('view_more')}
                        <ArrowIcon />
                    </div>
                )}
            </div>

            {/* 复制成功弹窗 */}
            <EfModal className={styles.couponModal} visible={shopPopVisible} centered onCancel={() => setShopPopVisible(false)}>
                <div className="content">
                    <div className="m-icon">
                        <SuccessIcon />
                    </div>
                    <h5 className="m-title">{referralT('copy_desc')}</h5>
                    <button className="m-btn" onClick={handleShopNow}>
                        {getShopButtonText()}
                    </button>
                </div>
            </EfModal>
        </div>
    );
}
